/* eslint-disable complexity */
import { OwFFormFieldKey } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/consts/index";
import { isNotPseudoKey } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/utils";
import { validateAddress } from "@faire/retailer-visitor-shared/lib/validation";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { useStrictLocalization } from "@faire/web/common/localization";
import { IAddress } from "@faire/web-api--source/indigofair/data/IAddress";
import { IRetailerLaunchWithFaireApplication } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaireApplication";
import isEmpty from "lodash/isEmpty";

type OwFFormFieldErrors = Partial<Record<OwFFormFieldKey, string>>;
interface IValidateArgs {
  formData: IRetailerLaunchWithFaireApplication;
  keysToValidate: Array<OwFFormFieldKey>;
  isResidentialAddressFormVisible?: boolean;
  isStoreAddressFormVisible?: boolean;
}
export const useValidatePartialFormData = () => {
  const { strictLocalize } = useStrictLocalization();
  const REQUIRED_STRING = strictLocalize({
    defaultMessage: "Required",
    description: { text: "Indicate the field in a form is required" },
  });

  const validatePartialFormData: (
    data: IValidateArgs
  ) => OwFFormFieldErrors = ({
    formData,
    keysToValidate,
    isResidentialAddressFormVisible,
    isStoreAddressFormVisible,
  }): OwFFormFieldErrors => {
    const addressFieldsToValidate = getAddressFieldsToValidate(formData);
    const initialErrors: OwFFormFieldErrors = {};

    const validationErrors: OwFFormFieldErrors = keysToValidate.reduce(
      (acc, field) => {
        if (field === "residential_address" || field === "store_address") {
          const residentialGooglePlaceId =
            field === "residential_address"
              ? formData["residential_address_google_place_id"]
              : undefined;
          const storeGooglePlaceId =
            field === "store_address"
              ? formData["store_address_google_place_id"]
              : undefined;

          if (
            (isResidentialAddressFormVisible &&
              field === "residential_address") ||
            (isStoreAddressFormVisible && field === "store_address")
          ) {
            const address = IAddress.build({
              ...(formData[field] || {}),
            });
            const isAddressInvalid = !isEmpty(
              validateAddress(addressFieldsToValidate, address)
            );
            if (isAddressInvalid) {
              acc[field] = REQUIRED_STRING;
            }
          } else if (
            field === "residential_address" &&
            !residentialGooglePlaceId
          ) {
            acc[field] = REQUIRED_STRING;
          } else if (field === "store_address" && !storeGooglePlaceId) {
            acc[field] = REQUIRED_STRING;
          }
        } else if (
          isNotPseudoKey(field) &&
          (field !== "monthly_rent" || formData[field] == null) &&
          !formData[field]
        ) {
          acc[field] = REQUIRED_STRING;
        } else if (
          field === "online_website_proof_documents" &&
          formData[field].length <= 0
        ) {
          acc[field] = REQUIRED_STRING;
        } else if (
          field === "popup_location_checkboxes" &&
          !formData["popup_location_events"] &&
          !formData["popup_location_brick_and_mortar"]
        ) {
          acc[field] = REQUIRED_STRING;
        }
        return acc;
      },
      initialErrors
    );

    return validationErrors;
  };

  return { validatePartialFormData };
};

const getAddressFieldsToValidate = (
  formData: IRetailerLaunchWithFaireApplication
): Array<string> => {
  const fields = ["country", "address1", "city", "postal_code"];
  const country = formData.store_address?.country;
  if (
    country === THREE_LETTER_PRIMARY_COUNTRY_CODES.USA ||
    country === THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN
  ) {
    fields.push("state");
  }
  return fields;
};
